import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DialogHapus } from '../components/DialogHapus';
import {
  dropExport,
  formatBytes,
  formatWib,
  listExports,
  type ExportJob,
} from '../lib/api';
import { useBahasa } from '../i18n';

/**
 * Download Center (`/downloads`, §G12).
 *
 * *"Yang sudah di export kan disimpen 30 menit tuh sampai sebelum dihapus.
 * Mending disimpen: 1 hari. Tapi ada download center. Kalo masih ada bisa di
 * download ulang. Kalo sudah tenggelam/hilang sejak birth time, jadi abu-abu,
 * tulisannya 'removed' gitu."*
 *
 * **Satu daftar, bukan tab per status.** Yang dicari orang di sini "berkas yang
 * tempo hari saya buat", dan ia tidak ingat apakah statusnya `ready` atau sudah
 * lewat umur — kalau ingat, ia tidak perlu membuka halaman ini.
 *
 * **Milik sendiri saja** (§F6). Tidak ada tampilan "punya semua orang" untuk
 * Admin: yang butuh berkas orang lain memintanya sendiri.
 */

/** §D8.9 — umur berkas sejak jadi. Dipakai menghitung sisa waktu di layar. */
const TTL_JAM = 24;

/**
 * Sisa waktu ditulis sebagai **sisa**, bukan sebagai tenggat (§G12).
 *
 * "Dihapus dalam 3 jam" bisa dibaca sambil lalu; "dihapus 01 Sep 2026 21:07"
 * harus dihitung dulu terhadap jam sekarang — dan itu justru saat orang sedang
 * buru-buru mengambil bukti.
 */
function sisaWaktu(readyAt: string, t: ReturnType<typeof useBahasa>['t']): { teks: string; mendesak: boolean } {
  const habis = new Date(readyAt).getTime() + TTL_JAM * 3_600_000;
  const sisaMs = habis - Date.now();
  if (sisaMs <= 0) return { teks: t('any moment now'), mendesak: true };
  const jam = Math.floor(sisaMs / 3_600_000);
  if (jam >= 1) return { teks: t('in {n}h', { n: jam }), mendesak: false };
  const menit = Math.max(1, Math.round(sisaMs / 60_000));
  return { teks: t('in {n} min', { n: menit }), mendesak: true };
}

function Chip({ job, t }: { job: ExportJob; t: ReturnType<typeof useBahasa>['t'] }) {
  const map: Record<string, { label: string; kelas: string }> = {
    queued: { label: t('queued'), kelas: 'border-line text-muted' },
    running: { label: t('running'), kelas: 'border-accent text-accent-text' },
    ready: { label: t('ready'), kelas: 'border-green-600 text-green-600' },
    failed: { label: t('failed'), kelas: 'border-red-600 text-red-600' },
    cancelled: { label: t('cancelled'), kelas: 'border-line text-muted' },
  };
  const s = job.removed
    ? { label: 'removed', kelas: 'border-line text-muted' }
    : (map[job.status] ?? map.queued!);
  return (
    <span className={`shrink-0 rounded border px-1.5 py-px text-xs ${s.kelas}`}>{s.label}</span>
  );
}

export function DownloadsPage() {
  const { t } = useBahasa();
  const [rows, setRows] = useState<ExportJob[] | null>(null);
  const [galat, setGalat] = useState<string | null>(null);
  /**
   * Berkas yang sedang dikonfirmasi penghapusannya (§G17).
   *
   * Hanya yang sudah selesai. Membatalkan yang masih antre/berjalan tetap satu
   * klik: tidak ada berkas yang hilang karenanya, dan yang salah minta tinggal
   * meminta lagi dengan rentang yang sama.
   */
  const [akanDihapus, setAkanDihapus] = useState<ExportJob | null>(null);

  const muat = useCallback(async () => {
    try {
      setRows(await listExports());
      setGalat(null);
    } catch (e) {
      setGalat((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void muat();
  }, [muat]);

  /**
   * Di-poll hanya selama ada yang berjalan, dan **berhenti sama sekali** kalau
   * tidak ada apa-apa (§G10.8). Polling yang terus berjalan di halaman yang
   * dibuka sepanjang hari adalah beban tetap untuk jawaban yang hampir selalu
   * "tidak ada apa-apa".
   */
  useEffect(() => {
    const sedang = rows?.some((r) => r.status === 'running' || r.status === 'queued');
    if (!sedang) return;
    const timer = window.setInterval(() => void muat(), 2000);
    return () => window.clearInterval(timer);
  }, [rows, muat]);

  if (rows === null) return <p className="p-8 text-sm text-muted">{t('Loading…')}</p>;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <h1 className="text-xl">{t('Download Center')}</h1>

      {galat && (
        <p role="alert" className="mt-4 rounded-md border border-red-600 px-3 py-2 text-sm">
          {galat}
        </p>
      )}

      {rows.length === 0 ? (
        // Halaman kosong yang tidak menyebutkan cara mengisinya adalah jalan
        // buntu (§G12).
        <div className="mt-8 rounded-lg border border-dashed border-line p-10 text-center">
          <p className="text-lg">{t('No exports yet.')}</p>
          <p className="mt-1 text-sm text-muted">
            {t('Create one from the Video page — pick a range, then press Download.')}{' '}
            <Link to="/videos" className="text-accent-text underline underline-offset-2">
              {t('Go to Video')}
            </Link>
          </p>
        </div>
      ) : (
        <ul className="mt-6 divide-y divide-line border-y border-line">
          {rows.map((r) => {
            const sisa = r.status === 'ready' && !r.removed && r.readyAt ? sisaWaktu(r.readyAt, t) : null;
            return (
              <li
                key={r.id}
                // `removed` diredupkan tapi TIDAK dibuang — seluruh alasan
                // halaman ini ada (§D8.9).
                className={`py-3 ${r.removed || r.status === 'cancelled' ? 'opacity-60' : ''}`}
              >
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium">{r.name ?? t('Untitled export')}</span>
                  <Chip job={r} t={t} />
                  <span className="text-sm text-muted">{r.cameraName}</span>
                  <span className="text-sm text-muted">
                    {formatWib(r.actualFrom ?? r.requestedFrom)} –{' '}
                    {formatWib(r.actualTo ?? r.requestedTo)}
                  </span>
                  {r.sizeBytes !== null && !r.removed && (
                    <span className="text-sm text-muted">{formatBytes(r.sizeBytes)}</span>
                  )}
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-3 text-sm">
                  {r.status === 'ready' && !r.removed && (
                    <>
                      <a
                        href={`/api/v1/exports/${r.id}/download`}
                        className="min-h-tap rounded-md bg-accent px-4 py-1.5 text-sm text-accent-contrast"
                      >
                        {t('Download')}
                      </a>
                      {sisa && (
                        <span className={sisa.mendesak ? 'text-amber-600' : 'text-muted'}>
                          {t('file deleted {sisa}', { sisa: sisa.teks })}
                        </span>
                      )}
                    </>
                  )}

                  {r.removed && (
                    <span className="text-muted">
                      {r.removedAt
                        ? t('file deleted on {waktu}', { waktu: formatWib(r.removedAt) })
                        : t('file already deleted')}
                    </span>
                  )}

                  {(r.status === 'queued' || r.status === 'running') && (
                    <span className="text-muted">
                      {r.status === 'queued'
                        ? r.queuePosition
                          ? t('Waiting — number {n} in the queue', { n: r.queuePosition })
                          : t('Waiting its turn')
                        : `${r.progressPct ?? 0}%`}
                    </span>
                  )}

                  {r.status === 'failed' && r.error && (
                    <span className="text-red-600">{r.error}</span>
                  )}

                  {/* "Export ulang" mengirim permintaan baru dengan kamera,
                      rentang, dan nama yang sama persis — lewat halaman Video,
                      yang memang tempat rentangnya dipilih. Barisnya yang lama
                      tetap di tempatnya sebagai riwayat (§D8.9). */}
                  {(r.removed || r.status === 'failed') && (
                    <Link
                      to={`/videos/${r.cameraId}`}
                      className="text-accent-text underline underline-offset-2"
                    >
                      {t('Export again')}
                    </Link>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      if (r.status === 'queued' || r.status === 'running') {
                        void dropExport(r.id)
                          .then(muat)
                          .catch((e) => setGalat((e as Error).message));
                        return;
                      }
                      setAkanDihapus(r);
                    }}
                    className="ml-auto text-xs text-muted underline underline-offset-2"
                  >
                    {r.status === 'queued' || r.status === 'running' ? t('Cancel') : t('Remove')}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {akanDihapus && (
        <DialogHapus
          // Nama yang diketik ulang = yang tertulis di barisnya. Export tanpa
          // nama jatuh ke nama kameranya, sama persis dengan yang diperiksa
          // server — kata "Untitled export" ikut berganti bahasa, dan server
          // tidak tahu bahasa apa yang sedang dipakai layar ini.
          nama={akanDihapus.name ?? akanDihapus.cameraName ?? ''}
          judul={t('Remove this download')}
          keterangan={
            akanDihapus.removed
              ? t('The file is already gone; this removes the row from the list.')
              : t(
                  'The prepared file is deleted from the server. If the recording it came from has passed retention, this may be the only copy left.',
                )
          }
          onBatal={() => setAkanDihapus(null)}
          onHapus={async (k) => {
            await dropExport(akanDihapus.id, k);
            setAkanDihapus(null);
            await muat();
          }}
        />
      )}

      {/* Ditulis apa adanya supaya tidak ada yang menganggap ini arsip (§G12). */}
      <p className="mt-6 text-xs text-muted">{t('History is kept for 7 days.')}</p>
    </div>
  );
}
